import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CTASection from "../components/CTASection";
import { Reveal } from "../components/Reveal";
import { GALLERY_IMAGES, IMAGES } from "../data/site";

export default function Gallery() {
  const [active, setActive] = useState(null);

  const open = (i) => setActive(i);
  const close = () => setActive(null);
  const prev = (e) => { e.stopPropagation(); setActive((v) => (v - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length); };
  const next = (e) => { e.stopPropagation(); setActive((v) => (v + 1) % GALLERY_IMAGES.length); };

  return (
    <div data-testid="gallery-page">
      <PageHeader
        overline="The Gallery"
        titleLines={["Real moments,", <span key="i" className="italic text-gold">real celebrations</span>]}
        subtitle="A look inside Vista Banquet Hall — our ballroom, in-house catering and décor, and the weddings and events we've had the honor to host."
        image={IMAGES.ceremony}
        imageAlt="Vista Banquet Hall celebration"
        testId="gallery-page-header"
      />

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32">
        <div className="columns-2 gap-4 md:columns-3 lg:columns-4 [column-fill:_balance]">
          {GALLERY_IMAGES.map((g, i) => (
            <Reveal key={g.src} delay={(i % 4) * 0.06} className="mb-4 break-inside-avoid">
              <button
                onClick={() => open(i)}
                data-testid={`gallery-item-${i}`}
                className="group relative block w-full overflow-hidden rounded-sm border border-gold/25"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-navy/0 transition-colors duration-500 group-hover:bg-navy/30" />
                <span className="absolute bottom-3 left-3 translate-y-2 opacity-0 overline text-ivory transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {g.cat}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/95 p-4 md:p-10"
          >
            <button data-testid="lightbox-close" onClick={close} className="absolute right-5 top-5 text-ivory/70 hover:text-ivory transition-colors">
              <X size={30} />
            </button>
            <button data-testid="lightbox-prev" onClick={prev} className="absolute left-3 md:left-8 text-ivory/60 hover:text-gold transition-colors">
              <ChevronLeft size={40} />
            </button>
            <button data-testid="lightbox-next" onClick={next} className="absolute right-3 md:right-8 text-ivory/60 hover:text-gold transition-colors">
              <ChevronRight size={40} />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw]"
            >
              <img src={GALLERY_IMAGES[active].src} alt={GALLERY_IMAGES[active].alt} className="max-h-[80vh] w-auto rounded-sm object-contain" />
              <figcaption className="mt-4 text-center overline text-gold">{GALLERY_IMAGES[active].cat}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      <CTASection
        overline="See It In Person"
        heading="Photos are lovely. In person is unforgettable."
        body="Schedule a private walkthrough of Vista Banquet Hall with our team."
        actions={[{ to: "/tour", label: "Schedule a Tour", primary: true }, { to: "/contact", label: "Contact Us" }]}
        testId="cta-gallery-page"
      />
    </div>
  );
}
